import type { WorkerClearancesResponse } from '../api';

/**
 * Раскладка карточки работника: что в какой раздел и в каком порядке.
 *
 * Здесь, как и в `personnel-rows.ts`, только оформление. Действует ли запись,
 * просрочена ли — решил сервер (`computedStatus`), и карточка этого не
 * пересчитывает.
 */

export type CardClearance = WorkerClearancesResponse['clearances'][number];

/** Вид допуска и все его записи — последняя первой. */
export type CardGroup = {
  typeId: string;
  name: string;
  kind: string | undefined;
  latest: CardClearance;
  history: CardClearance[];
};

export type CardSections = {
  briefings: CardGroup[];
  medical: CardGroup[];
  electrical: CardGroup[];
  /** Всё, что организация завела сверх трёх категорий, — чтобы не потерять. */
  other: CardGroup[];
};

// Issue #525: списка «все записи всех видов» здесь больше нет. Он собирается
// из журнала (`clearance-history.ts`, `recordLines`), потому что аннулированные
// записи в ответ светофора не попадают вовсе, а показать их надо — с пометкой
// и причиной. Два списка «все записи», один из которых молча теряет
// аннулированные, отвечали бы на один вопрос по-разному.

const byIssuedDesc = (a: CardClearance, b: CardClearance) => b.issuedAt.localeCompare(a.issuedAt);

/**
 * Порядок инструктажей — как они идут в жизни работника: вводный и
 * первичный (разовые), затем повторный (периодический), затем внеплановый
 * (событие). Вводный и первичный оба разовые и различаются только временем:
 * вводный проводят раньше, поэтому среди разовых — по дате первой записи.
 */
const KIND_ORDER: Record<string, number> = { ONE_TIME: 0, PERIODIC: 1, EVENT: 2 };

export function cardSections(response: Pick<WorkerClearancesResponse, 'clearances'>): CardSections {
  const groups = new Map<string, CardClearance[]>();
  for (const clearance of response.clearances) {
    const list = groups.get(clearance.clearanceTypeId) ?? [];
    list.push(clearance);
    groups.set(clearance.clearanceTypeId, list);
  }

  const sections: CardSections = { briefings: [], medical: [], electrical: [], other: [] };
  for (const [typeId, records] of groups) {
    const sorted = [...records].sort(byIssuedDesc);
    const latest = sorted[0];
    const group: CardGroup = {
      typeId,
      name: latest.clearanceType?.name ?? 'вид допуска не назван',
      kind: latest.clearanceType?.kind,
      latest,
      history: sorted,
    };
    switch (latest.clearanceType?.category) {
      case 'BRIEFING':
        sections.briefings.push(group);
        break;
      case 'MEDICAL':
        sections.medical.push(group);
        break;
      case 'ELECTRICAL_SAFETY':
        sections.electrical.push(group);
        break;
      default:
        sections.other.push(group);
    }
  }

  const firstIssued = (group: CardGroup) => group.history[group.history.length - 1].issuedAt;
  sections.briefings.sort(
    (a, b) =>
      (KIND_ORDER[a.kind ?? ''] ?? 9) - (KIND_ORDER[b.kind ?? ''] ?? 9)
      || firstIssued(a).localeCompare(firstIssued(b)),
  );
  return sections;
}

/**
 * Нужна ли у вида допуска пометка «Нет документа-основания» (issue #537).
 *
 * Пометку получает только ДЕЙСТВУЮЩАЯ запись: последняя по виду и не
 * истёкшая. Аннулированных в ответе светофора нет вовсе, заменённая
 * продлением — не последняя, а истёкшую не дозаполняют — её продлевают.
 * Нет ли основания, решил сервер (`basisMissing`).
 */
export function basisMissingMark(group: Pick<CardGroup, 'latest'>): boolean {
  return group.latest.basisMissing === true && group.latest.computedStatus !== 'expired';
}

/**
 * Состояние одной записи словами. Статус — сервера (`computedStatus`);
 * здесь только его название.
 */
export function recordStatusLabel(status: CardClearance['computedStatus']): string {
  switch (status) {
    case 'expired':
      return 'просрочен';
    case 'expiring_soon':
      return 'истекает';
    default:
      return 'действует';
  }
}

/** `2026-10-05` или ISO-момент → `05.10.2026`, текстом, без пояса. */
export function cardDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
}
